class HeroSlider extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.slides = this.querySelectorAll('.hero__slide');
    this.dots = this.querySelectorAll('.slider-counter__link');
    this.currentIndex = 0;
    this.autoplayInterval = null;
    this.videoTimeout = null;
    this.isPlaying = false;
    this.isPausedForVideo = false;
    this.isVisible = true;
  }

  connectedCallback() {
    if (!this.slider || this.slides.length < 2) return;

    this.autoplaySpeed = (parseInt(this.slider.dataset.speed, 10) || 5) * 1000;
    this.shouldAutoplay = this.slider.dataset.autoplay === 'true';

    this.setupDotListeners();
    this.setupTouchSwipe();
    this.setupIntersectionObserver();
    this.setInitialState();

    if (this.shouldAutoplay && !this.isPausedForVideo) {
      this.startAutoplay();
    }
  }

  disconnectedCallback() {
    this.stopAutoplay();
    if (this.videoTimeout) {
      clearTimeout(this.videoTimeout);
      this.videoTimeout = null;
    }
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
  }

  setInitialState() {
    this.slides.forEach((slide, index) => {
      if (index === 0) {
        slide.classList.add('hero__slide--active');
        slide.setAttribute('aria-hidden', 'false');
      } else {
        slide.classList.remove('hero__slide--active');
        slide.setAttribute('aria-hidden', 'true');
      }
    });

    this.updateDots();
    this.checkForVideoSlide(this.slides[0]);
  }

  setupDotListeners() {
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', (e) => {
        e.preventDefault();
        this.goToSlide(index);
      });
    });
  }

  setupTouchSwipe() {
    let touchStartX = 0;
    let touchEndX = 0;
    const minSwipeDistance = 50;

    this.slider.addEventListener('touchstart', (e) => {
      touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });

    this.slider.addEventListener('touchend', (e) => {
      touchEndX = e.changedTouches[0].screenX;
      const distance = touchStartX - touchEndX;

      if (Math.abs(distance) < minSwipeDistance) return;

      if (distance > 0) {
        this.nextSlide();
      } else {
        this.prevSlide();
      }

      if (this.shouldAutoplay && !this.isPausedForVideo) {
        this.restartAutoplay();
      }
    }, { passive: true });
  }

  setupIntersectionObserver() {
    this.intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const wasVisible = this.isVisible;
        this.isVisible = entry.isIntersecting;

        if (wasVisible !== this.isVisible) {
          if (this.isVisible && this.shouldAutoplay) {
            const currentSlide = this.slides[this.currentIndex];
            if (this.isVideoSlide(currentSlide)) {
              this.waitForVideoToComplete(currentSlide);
            } else if (!this.isPlaying) {
              this.startAutoplay();
            }
          } else if (!this.isVisible) {
            this.stopAutoplay();
            if (this.videoTimeout) {
              clearTimeout(this.videoTimeout);
              this.videoTimeout = null;
            }
            this.pauseAllVideos();
          }
        }
      });
    }, { threshold: 0.1 });

    this.intersectionObserver.observe(this);
  }

  isVideoSlide(slide) {
    return slide.hasAttribute('data-video-slide');
  }

  checkForVideoSlide(slide) {
    if (this.isVideoSlide(slide)) {
      this.isPausedForVideo = true;
      this.stopAutoplay();
      this.waitForVideoToComplete(slide);
    } else {
      this.isPausedForVideo = false;
      if (this.shouldAutoplay && this.isVisible) {
        this.startAutoplay();
      }
    }
  }

  startAutoplay() {
    if (this.slides.length < 2 || this.isPlaying) return;

    this.isPlaying = true;
    this.autoplayInterval = setInterval(() => {
      this.nextSlide();
    }, this.autoplaySpeed);
  }

  stopAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
    this.isPlaying = false;
  }

  restartAutoplay() {
    if (this.slides.length < 2) return;

    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }

    this.isPlaying = true;
    this.autoplayInterval = setInterval(() => {
      this.nextSlide();
    }, this.autoplaySpeed);
  }

  play() {
    if (!this.isPausedForVideo) {
      this.startAutoplay();
    }
  }

  pause() {
    this.stopAutoplay();
  }

  nextSlide() {
    const nextIndex = (this.currentIndex + 1) % this.slides.length;
    this.goToSlide(nextIndex);
  }

  prevSlide() {
    const prevIndex = (this.currentIndex - 1 + this.slides.length) % this.slides.length;
    this.goToSlide(prevIndex);
  }

  goToSlide(index) {
    if (index === this.currentIndex || index < 0 || index >= this.slides.length) return;

    const currentSlide = this.slides[this.currentIndex];
    const nextSlide = this.slides[index];

    currentSlide.classList.remove('hero__slide--active');
    currentSlide.setAttribute('aria-hidden', 'true');

    nextSlide.classList.add('hero__slide--active');
    nextSlide.setAttribute('aria-hidden', 'false');

    this.currentIndex = index;
    this.updateDots();
    this.pauseAllVideos();

    if (this.videoTimeout) {
      clearTimeout(this.videoTimeout);
      this.videoTimeout = null;
    }

    if (this.isVideoSlide(nextSlide)) {
      this.stopAutoplay();
      this.isPausedForVideo = true;
      this.waitForVideoToComplete(nextSlide);
    } else {
      this.isPausedForVideo = false;
      if (this.shouldAutoplay) {
        this.restartAutoplay();
      }
    }
  }

  updateDots() {
    this.dots.forEach((dot, index) => {
      if (index === this.currentIndex) {
        dot.classList.add('slider-counter__link--active');
      } else {
        dot.classList.remove('slider-counter__link--active');
      }
    });
  }

  pauseAllVideos() {
    this.slides.forEach((slide) => {
      const labVideo = slide.querySelector('lab-video');
      if (labVideo?.pauseVideo) {
        labVideo.pauseVideo();
      }
    });
  }

  waitForVideoToComplete(slide) {
    if (this.videoTimeout) {
      clearTimeout(this.videoTimeout);
      this.videoTimeout = null;
    }

    const labVideo = slide.querySelector('lab-video');
    const video = labVideo?.video || labVideo?.shadowRoot?.querySelector('video');

    let waitTime = this.autoplaySpeed;

    if (video) {
      video.currentTime = 0;
      video.play().catch(() => {});

      if (video.duration && isFinite(video.duration)) {
        waitTime = video.duration * 1000;
      }
    }

    this.videoTimeout = setTimeout(() => {
      if (this.slides[this.currentIndex] === slide && this.shouldAutoplay && this.isVisible) {
        this.isPausedForVideo = false;
        this.nextSlide();
      }
    }, waitTime);
  }
}

customElements.define('hero-slider', HeroSlider);
